<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useEquipmentsStore } from '@/store/equipments';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Sidebar');

const store = useEquipmentsStore();
const router = useRouter();

const loading = ref(true);
const loadingMore = ref(false);
const error = ref(null);

// Computed property for total categories count
const totalCategories = computed(() => store.categories.length);
const hasMore = computed(() => store.categoriesHasMore);
const remaining = computed(() => Math.max(0, store.categoriesTotal - store.categories.length));
const isExpanded = computed(() => store.categories.length > store.BATCH_SIZE);

// Calculate ad count with proper fallbacks
const getAdCount = (category) => Number(category?.ad_count) || 0;

// Navigate to category/subcategory and refresh page
const goToCategory = (slug) => {
  router.push({ name: 'category-details', query: { cat: slug } });
};

const loadMore = async () => {
  loadingMore.value = true;
  try {
    await store.fetchCategories(true);
  } catch (err) {
    logger.error('Load more categories failed', err);
  } finally {
    loadingMore.value = false;
  }
};

// Fetch first batch on mount
onMounted(async () => {
  try {
    loading.value = true;
    error.value = null;
    await store.fetchCategories();
  } catch (err) {
    logger.error('Component mount failed', err);
    error.value = err.response?.data?.message || 'Failed to fetch categories. Please try again later.';
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <aside class="sidebar-content max-h-[calc(100vh-120px)] overflow-y-auto" aria-label="Categories navigation">
    <!-- Loading Skeleton -->
    <div v-if="loading" class="p-3 space-y-0.5">
      <!-- Heading skeleton -->
      <div class="px-3 pt-2 pb-3 mb-1">
        <div class="flex items-center gap-1.5">
          <div class="w-4 h-4 rounded bg-theme-bg2 relative overflow-hidden shrink-0"><div class="skeleton-shimmer"></div></div>
          <div class="h-5 bg-theme-bg2 rounded-full w-36 relative overflow-hidden"><div class="skeleton-shimmer"></div></div>
        </div>
        <div class="h-3 bg-theme-bg2 rounded-full w-44 mt-2 relative overflow-hidden"><div class="skeleton-shimmer"></div></div>
      </div>

      <!-- Category rows with subcategories -->
      <div v-for="i in 6" :key="`skel-cat-${i}`" class="space-y-0">
        <!-- Parent category row -->
        <div class="flex items-center justify-between px-3 py-2.5 border-l-2 border-transparent">
          <div class="h-4 bg-theme-bg2 rounded-full relative overflow-hidden" :style="{ width: [65,80,55,72,60,48][i-1] + '%' }">
            <div class="skeleton-shimmer"></div>
          </div>
          <div class="h-5 w-16 bg-theme-bg2 rounded-full relative overflow-hidden shrink-0 ml-2">
            <div class="skeleton-shimmer"></div>
          </div>
        </div>
        <!-- Subcategory rows (show for some categories) -->
        <template v-if="i <= 3">
          <div v-for="j in (i === 1 ? 3 : i === 2 ? 2 : 1)" :key="`skel-sub-${i}-${j}`"
            class="flex items-center justify-between pl-7 pr-3 py-1.5">
            <div class="h-3 bg-theme-bg2 rounded-full relative overflow-hidden" :style="{ width: [50,70,42,58,45,65][j-1] + '%' }">
              <div class="skeleton-shimmer"></div>
            </div>
            <div class="h-3.5 w-10 bg-theme-bg2 rounded-full relative overflow-hidden shrink-0 ml-2">
              <div class="skeleton-shimmer"></div>
            </div>
          </div>
        </template>
      </div>

      <!-- See More button skeleton -->
      <div class="mt-2 flex justify-center">
        <div class="h-8 w-32 bg-theme-bg2 rounded-lg relative overflow-hidden"><div class="skeleton-shimmer"></div></div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-500/10 rounded-lg">
      <p class="text-red-500 text-sm flex items-center">
        <i class="pi pi-exclamation-circle mr-1.5"></i>
        {{ error }}
      </p>
      <button @click="store.fetchCategories" class="mt-2 text-xs text-primary-500 hover:text-primary-600 focus:outline-none">
        Retry
      </button>
    </div>

    <!-- Success State -->
    <template v-else>
      <div v-if="totalCategories > 0" class="categories-container">
        <!-- Heading -->
        <div class="px-3 pt-2 pb-3 mb-1">
          <h2 class="text-base font-bold text-theme-text tracking-wide flex items-center gap-1.5">
            <i class="pi pi-th-large text-primary-500 text-sm"></i>
            Browse Categories
          </h2>
          <p class="text-sm text-theme-muted mt-0.5">Find exactly what you need</p>
        </div>

        <ul class="space-y-0.5">
          <li v-for="category in store.categories" :key="`category-${category.id}`" class="group">
            <button
              @click="goToCategory(category.slug)"
              class="cat-btn w-full text-left flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 hover:bg-theme-bg2 hover:pl-4 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
              :aria-label="`Browse ${category.name} category (${getAdCount(category)} listings)`"
            >
              <span class="text-base font-medium text-theme-text truncate">{{ category.name }}</span>
              <span class="text-sm text-theme-muted tabular-nums ml-2 shrink-0 bg-theme-bg2 group-hover:bg-primary-500/10 group-hover:text-primary-600 px-2 py-0.5 rounded-full transition-all duration-200">
                {{ getAdCount(category) }} {{ getAdCount(category) === 1 ? 'listing' : 'listings' }}
              </span>
            </button>

            <!-- Subcategories -->
            <ul v-if="category.subcategories?.length > 0" class="subcats overflow-hidden max-h-0 group-hover:max-h-[500px] transition-[max-height] duration-300 ease-in-out">
              <li v-for="subcategory in category.subcategories" :key="`subcategory-${subcategory.id}`">
                <button
                  @click.stop="goToCategory(subcategory.slug)"
                  class="w-full text-left flex items-center justify-between pl-7 pr-3 py-2 rounded-md transition-all duration-200 hover:bg-theme-bg2 hover:pl-8 focus:outline-none focus:ring-1 focus:ring-primary-500/30"
                  :aria-label="`Browse ${subcategory.name} subcategory (${subcategory.ad_count} listings)`"
                >
                  <span class="text-sm text-theme-text2 truncate">{{ subcategory.name }}</span>
                  <span class="text-sm text-theme-muted tabular-nums ml-2 shrink-0">{{ (subcategory.ad_count || 0) }} {{ (subcategory.ad_count || 0) === 1 ? 'listing' : 'listings' }}</span>
                </button>
              </li>
            </ul>
          </li>
        </ul>

        <!-- See More -->
        <button
          v-if="hasMore"
          @click="loadMore"
          :disabled="loadingMore"
          class="w-full mt-2 py-2 text-sm font-semibold text-primary-600 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-600/10 rounded-lg transition-all flex items-center justify-center gap-1 disabled:opacity-50"
        >
          <i v-if="loadingMore" class="pi pi-spin pi-spinner text-xs"></i>
          <i v-else class="pi pi-chevron-down text-xs"></i>
          {{ loadingMore ? 'Loading...' : `See More (${remaining})` }}
        </button>

        <!-- See Less -->
        <button
          v-if="isExpanded && !hasMore"
          @click="store.collapseCategories()"
          class="w-full mt-2 py-2 text-sm font-semibold text-primary-600 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-600/10 rounded-lg transition-all flex items-center justify-center gap-1"
        >
          <i class="pi pi-chevron-up text-xs"></i>
          See Less
        </button>
      </div>

      <!-- Empty State -->
      <div v-else class="p-4 text-center text-theme-muted text-sm">
        No categories available
      </div>
    </template>
  </aside>
</template>

<style scoped>
.sidebar-content {
  @apply w-full;
  scrollbar-width: thin;
  scrollbar-color: var(--color-primary-600, #ff6f00) transparent;
}

.sidebar-content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background-color: var(--color-primary-600, #ff6f00);
  border-radius: 10px;
}

.cat-btn {
  border-left: 2px solid transparent;
  transition: all 0.2s ease, padding-left 0.2s ease, border-color 0.2s ease;
}

.cat-btn:hover {
  border-left-color: var(--color-primary-500, #ff9c00);
}

.skeleton-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 20%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.08) 80%, transparent 100%);
  animation: shimmer 1.8s ease-in-out infinite;
}

:root:not(.dark) .skeleton-shimmer {
  background: linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.03) 20%, rgba(0,0,0,0.06) 50%, rgba(0,0,0,0.03) 80%, transparent 100%);
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>
